import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService } from '@/services/authService'
import { api } from '@/services/api'
import { User, RegisterRequest } from '@/types'

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // Actions
  login: (email: string, password: string, loginType?: 'member' | 'staff' | 'admin') => Promise<any>
  register: (userData: RegisterRequest) => Promise<void>
  logout: () => void
  refreshAuth: () => Promise<void>
  checkAuth: () => Promise<void>
  clearError: () => void
  updateUser: (userData: Partial<User>) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string, loginType: 'member' | 'staff' | 'admin' = 'member') => {
        try {
          set({ isLoading: true, error: null })
          
          // Determine the correct API endpoint based on login type
          let response
          if (loginType === 'admin') {
            response = await authService.adminLogin({ email, password })
          } else if (loginType === 'staff') {
            response = await authService.staffLogin({ email, password })
          } else {
            response = await authService.login({ email, password })
          }
          
          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
          
          return response
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Login failed'
          })
          throw error
        }
      },

      register: async (userData: RegisterRequest) => {
        try {
          set({ isLoading: true, error: null })
          
          const response = await authService.register(userData)
          
          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Registration failed'
          })
          throw error
        }
      },

      logout: () => {
        // Clear auth data
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null
        })
        
        // Clear any stored data
        localStorage.removeItem('auth-storage')
        
        // Clear any auth headers
        delete api.defaults.headers.common['Authorization']
      },

      refreshAuth: async () => {
        try {
          const { refreshToken } = get()
          
          if (!refreshToken) {
            throw new Error('No refresh token available')
          }
          
          const response = await authService.refreshToken(refreshToken)
          
          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken,
            isAuthenticated: true
          })
          
          // Update auth header
          api.defaults.headers.common['Authorization'] = `Bearer ${response.token}`
        } catch (error) {
          // If refresh fails, logout user
          get().logout()
          throw error
        }
      },

      checkAuth: async () => {
        try {
          const { token } = get()
          
          if (!token) {
            set({ isAuthenticated: false, isLoading: false })
            return
          }
          
          set({ isLoading: true })
          
          // Set auth header
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          // Verify token by getting current user
          const user = await authService.getCurrentUser()
          
          set({
            user,
            isAuthenticated: true,
            isLoading: false
          })
        } catch (error) {
          // If token is invalid, try to refresh
          try {
            await get().refreshAuth()
          } catch (refreshError) {
            // If refresh also fails, logout
            get().logout()
          }
          set({ isLoading: false })
        }
      },

      clearError: () => {
        set({ error: null })
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get()
        if (user) {
          set({
            user: { ...user, ...userData }
          })
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated
      }),
      onRehydrateStorage: () => (state) => {
        // Set auth header when rehydrating
        if (state?.token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`
        }
      }
    }
  )
)

// Selectors for better performance
export const useUser = () => useAuthStore((state) => state.user)
export const useToken = () => useAuthStore((state) => state.token)
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated)
export const useIsLoading = () => useAuthStore((state) => state.isLoading)
export const useAuthError = () => useAuthStore((state) => state.error)

export const useAuthActions = () => useAuthStore((state) => ({
  login: state.login,
  register: state.register,
  logout: state.logout,
  refreshAuth: state.refreshAuth,
  checkAuth: state.checkAuth,
  clearError: state.clearError,
  updateUser: state.updateUser
}))
